import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, User } from "lucide-react";
import { DYNTRA_LOGO_URL } from "@/lib/assets";
import { useAuth } from "@/contexts/AuthContext";

const STEP_DEFS = [
  { key: "cart", label: "Cart" },
  { key: "delivery", label: "Delivery" },
  { key: "payment", label: "Payment" },
];

/**
 * Slim, focused header used by /checkout and /order/:orderNo.
 * Shows the brand, a 3-step progress and a "Secure Checkout" badge.
 *
 * Props:
 *   - variant?: "checkout" | "confirmation" (default "checkout")
 *   - activeStep?: "cart" | "delivery" | "payment"  (checkout only)
 */
export default function MinimalHeader({ variant = "checkout", activeStep = "payment" }) {
  const { user } = useAuth();
  const initials = user?.name
    ? user.name
        .split(" ")
        .map((w) => w.trim()[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : null;

  const activeIndex = STEP_DEFS.findIndex((s) => s.key === activeStep);

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
      <div className="dyntra-container h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2">
          <img
            src={DYNTRA_LOGO_URL}
            alt="Dyntra"
            className="h-9"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <span className="sr-only">Dyntra</span>
        </Link>

        {variant === "checkout" ? (
          <div className="hidden md:flex items-center gap-3">
            {STEP_DEFS.map((s, i) => {
              const done = i < activeIndex;
              const active = i === activeIndex;
              return (
                <React.Fragment key={s.key}>
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        done
                          ? "bg-emerald-500 text-white"
                          : active
                            ? "bg-[#4B2AAD] text-white"
                            : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {done ? "✓" : i + 1}
                    </span>
                    <span
                      className={`text-xs font-semibold ${
                        active ? "text-gray-900" : "text-gray-500"
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < STEP_DEFS.length - 1 && (
                    <span
                      className={`h-px w-6 ${
                        i < activeIndex ? "bg-emerald-500" : "bg-gray-200"
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <p className="hidden md:block text-sm font-semibold text-gray-700">
            Order Confirmation
          </p>
        )}

        <div className="flex items-center gap-3">
          {variant === "checkout" ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 px-3 py-1 text-xs font-bold">
              <ShieldCheck className="h-3.5 w-3.5" /> Secure Checkout
            </div>
          ) : (
            <Link
              to="/account/help"
              className="text-xs font-semibold text-gray-600 hover:text-[#4B2AAD]"
            >
              Need help?
            </Link>
          )}
          {initials ? (
            <div className="h-8 w-8 rounded-full bg-[#F3EFFC] text-[#4B2AAD] flex items-center justify-center text-xs font-bold">
              {initials}
            </div>
          ) : (
            <div className="h-8 w-8 rounded-full bg-[#F3EFFC] text-[#4B2AAD] flex items-center justify-center">
              <User className="h-4 w-4" />
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
